/**
 * @file Règles des réponses aux questions personnalisées, partagées par le widget, le site public et le backoffice.
 * @module common.utils.restaurant.customAnswers
 *
 * Les formulaires tiennent les réponses dans un objet `{questionId: valeur}`,
 * pratique pour lier un champ à sa question (jamais comme chemins RHF : un
 * identifiant fait de chiffres serait lu comme un index de tableau). L'API attend une liste
 * `[{questionId, value}]` et renvoie des réponses figées
 * `[{questionId, type, label, value}]`. Ce fichier fait le pont dans les deux
 * sens et porte la règle « la question a une réponse », identique à celle du
 * serveur (`reservationCustomAnswersService`) : un texte vide, une case non
 * cochée ou un Oui/Non non choisi ne sont pas des réponses.
 */

import {CUSTOM_QUESTION_TYPES} from 'packages/common/constants/restaurants/customQuestions';

/**
 * Indique si une valeur vaut réponse pour un type de question.
 *
 * @param {string} type - Type de la question (`CUSTOM_QUESTION_TYPES`)
 * @param {*} value - Valeur saisie
 * @return {boolean} true si la valeur sera enregistrée comme réponse
 */
export function isCustomAnswerProvided(type, value) {
  switch (type) {
    case CUSTOM_QUESTION_TYPES.TEXT:
      return typeof value === 'string' && value.trim() !== '';
    case CUSTOM_QUESTION_TYPES.YES_NO:
      return typeof value === 'boolean';
    case CUSTOM_QUESTION_TYPES.CHECKBOX:
      return value === true;
    default:
      return false;
  }
}

/**
 * Liste les questions obligatoires restées sans réponse.
 *
 * @param {Array<{id: string, type: string, required: boolean}>} questions - Questions posées
 * @param {Object<string, *>} [valuesById] - Réponses saisies par identifiant de question
 * @return {Array<Object>} Questions obligatoires sans réponse
 */
export function findUnansweredRequiredQuestions(questions, valuesById) {
  return (questions || []).filter((question) => question.required && !isCustomAnswerProvided(question.type, valuesById?.[question.id]));
}

/**
 * Liste les questions réellement posées d'une configuration restaurant complète,
 * sous la forme exposée aux surfaces client (`{id, type, required, label}`).
 *
 * Miroir de la règle serveur `getAskedCustomQuestions` : une question est posée
 * si elle est activée et libellée dans au moins une langue. Sert au backoffice,
 * qui lit la configuration complète plutôt que la vue publique.
 *
 * @param {Object} [config] - Configuration restaurant (`customQuestions`)
 * @return {Array<{id: string, type: string, required: boolean, label: Object}>} Questions posées
 */
export function getAskedCustomQuestions(config) {
  return (config?.customQuestions || [])
      .filter((question) => question && question.enabled !== false && Object.values(question.label || {}).some((text) => typeof text === 'string' && text.trim() !== ''))
      .map((question) => ({
        id: String(question._id),
        type: question.type,
        required: question.required === true,
        askInBackoffice: question.askInBackoffice !== false,
        label: question.label,
      }));
}

/**
 * Liste les questions affichées dans un formulaire de réservation de l'équipe.
 *
 * Même règle que les champs standards (`backofficeFieldVisibility`) : une question
 * que l'établissement ne propose pas à son équipe reste affichée dès que la
 * réservation porte déjà une réponse. Sinon le formulaire de modification, qui
 * renvoie les réponses des questions affichées, effacerait celle du client.
 *
 * @param {Object} [config] - Configuration restaurant (`customQuestions`)
 * @param {Object|null} [reservation] - Réservation modifiée, omise à la création
 * @return {Array<Object>} Questions à afficher, au format de `getAskedCustomQuestions`
 */
export function getBackofficeCustomQuestions(config, reservation = null) {
  const answeredIds = new Set((reservation?.customAnswers || []).map((answer) => String(answer.questionId)));
  return getAskedCustomQuestions(config).filter((question) => question.askInBackoffice || answeredIds.has(question.id));
}

/** Valeurs des boutons radio d'une question Oui / Non, le formulaire ne tenant que des chaînes. */
export const YES_NO_CHOICES = Object.freeze({YES: 'yes', NO: 'no'});

/**
 * Traduit une réponse Oui / Non en bouton radio sélectionné.
 *
 * @param {*} value - Réponse saisie (`true`, `false`, ou rien tant que le client n'a pas choisi)
 * @return {string} Valeur du bouton sélectionné, vide sans choix
 */
export function toYesNoChoice(value) {
  if (value === true) return YES_NO_CHOICES.YES;
  if (value === false) return YES_NO_CHOICES.NO;
  return '';
}

/**
 * Traduit le bouton radio sélectionné en réponse Oui / Non.
 *
 * @param {string} choice - Valeur du bouton sélectionné
 * @return {boolean} true pour Oui
 */
export function fromYesNoChoice(choice) {
  return choice === YES_NO_CHOICES.YES;
}

/**
 * Rend une réponse enregistrée en texte lisible.
 *
 * Une case cochée se lit comme un « oui », comme dans les emails. Les mots
 * oui/non sont fournis par l'appelant ; par défaut, ceux du backoffice,
 * rédigé en français.
 *
 * @param {Object} answer - Réponse figée (`{type, value}`)
 * @param {Object} [options] - Options
 * @param {string} [options.yes='Oui'] - Mot affiché pour une réponse positive
 * @param {string} [options.no='Non'] - Mot affiché pour une réponse négative
 * @return {string} Réponse affichable
 */
export function formatCustomAnswerValue(answer, {yes = 'Oui', no = 'Non'} = {}) {
  if (answer.type === CUSTOM_QUESTION_TYPES.TEXT) return String(answer.value);
  return answer.value ? yes : no;
}

/**
 * Construit la liste envoyée à l'API. Le serveur fait autorité : il ignore les
 * questions inconnues, nettoie les textes et écarte ce qui n'est pas une réponse.
 *
 * @param {Object<string, *>} [valuesById] - Réponses saisies par identifiant de question
 * @return {Array<{questionId: string, value: *}>} Réponses à envoyer
 */
export function buildCustomAnswersPayload(valuesById) {
  return Object.entries(valuesById || {}).map(([questionId, value]) => ({questionId, value}));
}

/**
 * Ramène des réponses enregistrées à l'objet `{questionId: valeur}` des formulaires.
 *
 * Seule une réponse donnée au type actuel de sa question pré-remplit le champ.
 * Un « Oui » donné à une question devenue case à cocher ne doit jamais
 * reparaître coché : renvoyé tel quel, il serait enregistré comme un accord que
 * le client n'a pas donné. Le serveur garde l'ancienne réponse en historique.
 *
 * @param {Array<{questionId: string, type: string, value: *}>} [customAnswers] - Réponses enregistrées
 * @param {Array<{id: string, type: string}>} [questions] - Questions affichées par le formulaire
 * @return {Object<string, *>} Réponses par identifiant de question
 */
export function toCustomAnswerValues(customAnswers, questions) {
  const typeById = new Map((questions || []).map((question) => [question.id, question.type]));
  return (customAnswers || []).reduce((values, answer) => {
    const questionId = String(answer.questionId);
    if (typeById.get(questionId) === answer.type) values[questionId] = answer.value;
    return values;
  }, {});
}
